import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, Loader2, MapPin, CheckCircle2, Truck, Copy, ExternalLink } from 'lucide-react';
import { rastrear, listarEnvios, rastreioCota } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const brData = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const statusCor = { 'Entregue': 'bg-emerald-100 text-emerald-700', 'Em trânsito': 'bg-blue-100 text-blue-700', 'Saiu para entrega': 'bg-orange-100 text-orange-700', 'Aguardando postagem': 'bg-amber-100 text-amber-700' };

const RastreioPage = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const inicial = (new URLSearchParams(search).get('codigo') || '').toUpperCase();
  const [codigo, setCodigo] = useState(inicial);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [erro, setErro] = useState('');
  const [recentes, setRecentes] = useState([]);
  const [cota, setCota] = useState(null);

  useEffect(() => {
    listarEnvios().then((l) => setRecentes((l || []).filter((e) => e.codigo_objeto && e.status !== 'Cancelado' && e.status !== 'Cancelando').slice(0, 8))).catch(() => {});
  }, []);

  useEffect(() => { rastreioCota().then(setCota).catch(() => {}); }, []);

  const consultar = useCallback(async (cod) => {
    const c = String(cod || '').trim().toUpperCase();
    if (!c) { setErro('Digite o código de rastreio.'); return; }
    setLoading(true); setErro(''); setRes(null);
    try {
      const dados = await rastrear(c);
      setRes(dados);
      if (dados.cota_externa) setCota(dados.cota_externa);
    } catch (err) {
      if (err?.response?.status !== 401) setErro(err?.response?.data?.detail || 'Não foi possível consultar agora. Tente novamente.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (inicial) consultar(inicial); }, [inicial, consultar]);

  const enviar = (e) => {
    e.preventDefault();
    navigate(`/rastreio?codigo=${encodeURIComponent(codigo.trim().toUpperCase())}`, { replace: true });
    consultar(codigo);
  };
  const copiar = async (texto) => {
    try { await navigator.clipboard.writeText(texto); toast({ title: 'Código copiado' }); } catch { /* noop */ }
  };

  return (
    <AppShell title="Rastreio" subtitle="Consulte qualquer código de rastreio dos Correios">
      <div className="max-w-3xl space-y-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <form onSubmit={enviar} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase())} placeholder="Código de rastreio (ex.: AP123456789BR)" maxLength={13} className="pl-9 h-11 uppercase tracking-wide" />
            </div>
            <Button type="submit" disabled={loading} className="inn-btn-primary h-11 rounded-full px-6">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Consultando...</> : 'Rastrear'}
            </Button>
          </form>
          {recentes.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-slate-500 mb-2">Seus envios mais recentes:</p>
              <div className="flex flex-wrap gap-2">
                {recentes.map((e) => (
                  <button key={e.id} type="button" onClick={() => { setCodigo(e.codigo_objeto); navigate(`/rastreio?codigo=${e.codigo_objeto}`, { replace: true }); consultar(e.codigo_objeto); }}
                    className={`text-left text-xs rounded-lg border px-3 py-2 hover:border-[#2A5BC7] transition-colors ${res?.codigo === e.codigo_objeto ? 'border-[#2A5BC7] bg-[#EAF1FF]' : 'border-slate-200 bg-white'}`}>
                    <div className="font-semibold text-slate-800">{e.codigo_objeto}</div>
                    <div className="text-slate-500 truncate max-w-[180px]">{e.recipient_name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {cota?.ativo && !cota.ilimitada && (
            <p className={`text-xs mt-4 ${cota.usadas >= cota.limite ? 'text-amber-700' : 'text-slate-500'}`}>
              Códigos de <strong>outros contratos</strong> consultados neste mês: <strong>{cota.usadas} de {cota.limite}</strong>. Repetir o mesmo código não conta.
            </p>
          )}
        </div>

        {erro && <p className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{erro}</p>}

        {res && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-extrabold text-slate-900 tracking-wide">{res.codigo}</span>
                  <button type="button" onClick={() => copiar(res.codigo)} className="text-slate-400 hover:text-[#2A5BC7]" aria-label="Copiar código"><Copy className="w-4 h-4" /></button>
                  <a href={`https://rastreamento.correios.com.br/app/index.php?objetos=${res.codigo}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#2A5BC7]" title="Abrir no site dos Correios"><ExternalLink className="w-4 h-4" /></a>
                </div>
                <p className="text-sm text-slate-600 mt-1">{res.servico}{res.destinatario ? ` · ${res.destinatario}` : ''}{res.destino ? ` · ${res.destino}` : ''}</p>
              </div>
              {res.status && <Badge className={`${statusCor[res.status] || 'bg-slate-100 text-slate-700'} border-0 font-medium`}>{res.status}</Badge>}
            </div>

            {res.sem_historico ? (
              <div className="mt-4 rounded-xl bg-amber-50 text-amber-900 p-4">
                <p className="text-sm">{res.aviso}</p>
                <a href={res.link_correios} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-3 rounded-full bg-[#2A5BC7] text-white text-sm font-semibold px-4 py-2 hover:bg-[#1E429F]"><ExternalLink className="w-4 h-4" /> Consultar no site dos Correios</a>
              </div>
            ) : null}

            {!res.sem_historico && (<div className={`mt-4 rounded-xl p-4 flex items-center gap-3 ${res.entregue ? 'bg-emerald-50 text-emerald-800' : 'bg-[#EAF1FF] text-[#1E429F]'}`}>
              {res.entregue ? <CheckCircle2 className="w-6 h-6" /> : <Truck className="w-6 h-6" />}
              <div>
                {res.entregue
                  ? <><div className="font-bold">Entregue ao destinatário</div><div className="text-sm">em {brData(res.eventos[0]?.data)} às {res.eventos[0]?.hora}</div></>
                  : <><div className="text-sm">Previsão de entrega</div><div className="font-bold text-lg">{res.previsao_entrega ? brData(res.previsao_entrega) : 'a confirmar'}</div></>}
              </div>
            </div>)}

            {!res.sem_historico && <h3 className="font-bold text-slate-900 mt-6 mb-3">Histórico</h3>}
            {!res.sem_historico && res.eventos.length ? (
              <ol className="relative border-l-2 border-slate-100 ml-2 space-y-5">
                {res.eventos.map((e, i) => (
                  <li key={`${e.data}-${e.hora}-${i}`} className="pl-5 relative">
                    <span className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full ${i === 0 ? 'bg-[#2A5BC7]' : 'bg-slate-300'}`} />
                    <div className="text-xs text-slate-500">{brData(e.data)} · {e.hora}</div>
                    <div className={`${i === 0 ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>{e.descricao}</div>
                    {(e.local || e.para) && <div className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.local}{e.para ? ` → ${e.para}` : ''}</div>}
                    {e.detalhe && <div className="text-xs text-slate-400 mt-0.5">{e.detalhe}</div>}
                  </li>
                ))}
              </ol>
            ) : null}
            {res.fonte === 'externa' && (
              <p className="text-xs text-slate-400 mt-5">Código de outro contrato dos Correios, consultado por serviço de rastreio externo.{!res.historico_completo ? ' Mostrando apenas o evento mais recente; para ver o histórico completo, abra no site dos Correios.' : ''}</p>
            )}
            {res.fonte === 'externa' && !res.historico_completo && (
              <a href={res.link_correios} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-3 text-sm font-semibold text-[#2A5BC7] hover:underline"><ExternalLink className="w-4 h-4" /> Ver histórico completo no site dos Correios</a>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default RastreioPage;
